/**
 * CommandPalette — the ⌘K "go anywhere" surface (bound app-wide in App.tsx).
 *
 * 2026-07-31 rebuild: the "Go to …" commands are DERIVED from the nav tree
 * (`config/navTree.ts` → resolveNav) instead of a hardcoded list, so the
 * palette can never again drift from the rail — same labels, same routes,
 * same permission gating (hide-don't-lock), and the same glyphs via
 * `config/navIcons.tsx`. The pre-rebuild list dated from the pre-rail-v2 app:
 * it offered sunset surfaces ("Go to Phones" landed on the Dashboard), legacy
 * names, and clock icons on non-clock tools.
 *
 * On top of the tree: the personal trio (My Tickets → the Support Tickets
 * table's built-in 'my-tickets' saved view; My Scorecard / My Time → the
 * me-scoped personal pages), Settings, the three theme switches, and Sync All.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import { resolveNav, type NavGateContext } from '../config/navTree';
import { NAV_ICONS, navIcon } from '../config/navIcons';
import { useAuth } from '../contexts/AuthContext';
import { searchApi } from '../services/api';
import type { GlobalSearchResults } from '../services/api';
import { debugService } from '../services/debugService';
import type { ThemeMode } from '../theme';

type NavCommand = {
  type: 'nav';
  label: string;
  icon: React.ReactNode;
  /** Where Enter navigates. Omitted for pure actions (theme, sync). */
  path?: string;
  action?: () => void;
  /** The rail group the destination lives under — shown as the row's hint. */
  group?: string;
  keywords: string;
};

type EntityCommand = {
  type: 'entity';
  group: string;
  id: string | number;
  label: string;
  path: string;
  icon: React.ReactNode;
  /** A quiet right-edge caption (a contact's company / email / phone) — the same slot a nav row's group uses. */
  hint?: string;
};

type CommandItem = NavCommand | EntityCommand;

function getRecentCommands(): string[] {
  try {
    const raw = localStorage.getItem('bsg-recent-commands');
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, 5) : [];
  } catch {
    return [];
  }
}

function addRecentCommand(label: string) {
  const recent = getRecentCommands();
  const updated = [label, ...recent.filter(l => l !== label)].slice(0, 5);
  localStorage.setItem('bsg-recent-commands', JSON.stringify(updated));
}

// Old names people still type → the surface that answers today. Keyed by the
// nav item's stable `key`; merged into that entry's search keywords so
// "phones" finds Comms and "proposals" finds Contracts.
const LEGACY_KEYWORDS: Record<string, string> = {
  Comms: 'phones calls sms text dialer voicemail',
  Proposals: 'proposals esign sign',
  PersonalInbox: 'personal inbox mail',
  'Call Intelligence': 'call intelligence fathom transcript notes review',
  Scheduling: 'scheduling booking links',
  Capacity: 'capacity forecast forecasting',
  KnowledgeBase: 'knowledge base wiki',
  Dashboard: 'dashboard home center',
  'Time Tracking': 'time tracking hours timer clock',
  Todos: 'todo todos tasks',
  HelpDesk: 'helpdesk support inbox threads',
  AiMissionControl: 'ai mission control agents',
  Scorecards: 'scorecard metrics kpi',
};

export default function CommandPalette({
  open,
  onClose,
  mode,
  onSetMode,
  onSyncAll,
}: {
  open: boolean;
  onClose: () => void;
  mode: ThemeMode;
  onSetMode: (m: ThemeMode) => void;
  onSyncAll: () => void;
}) {
  const navigate = useNavigate();
  const auth = useAuth();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<GlobalSearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedItemRef = useRef<HTMLLIElement>(null);
  const selectedIndexRef = useRef(0);

  const closePalette = useCallback(() => {
    onClose();
    setSearch('');
    setResults(null);
    setLoading(false);
    setSelectedIndex(0);
    selectedIndexRef.current = 0;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setResults(null);
    setLoading(false);
    setSelectedIndex(0);
    selectedIndexRef.current = 0;
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  useEffect(() => {
    setSelectedIndex(0);
    selectedIndexRef.current = 0;
  }, [search]);

  useEffect(() => {
    if (search.length < 3) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timeout = setTimeout(() => {
      searchApi.search(search)
        .then(data => setResults(data))
        .catch((err: any) => {
          debugService.logAction('dashboard_command_palette_search_failed', { error: err?.message }, 'warn');
          setResults(null);
        })
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    if (open) {
      selectedItemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [open, selectedIndex]);

  // The same thin auth slice AppShell hands the rail — the palette resolves
  // the SAME tree, so an item the rail hides is absent here too.
  const navCtx = useMemo<NavGateContext>(() => ({
    hasTab: (t: string) => auth.hasTab(t),
    hasAction: (a: string) => auth.hasAction(a),
    isAdmin: () => auth.isAdmin(),
    isTeamMember: () => auth.isTeamMember(),
  }), [auth]);

  const commands = useMemo<NavCommand[]>(() => {
    const out: NavCommand[] = [];
    const seen = new Set<string>();
    const push = (label: string, iconKey: string, path: string, group: string | undefined, extraKeywords = '') => {
      const dedupeKey = `${label}→${path}`;
      if (seen.has(dedupeKey)) return; // e.g. Scorecards appears under EOS and Analytics
      seen.add(dedupeKey);
      out.push({
        type: 'nav',
        label: `Go to ${label}`,
        icon: navIcon(iconKey),
        path,
        group,
        keywords: `${label} ${group ?? ''} ${path.replace(/[/?=-]+/g, ' ')} ${extraKeywords}`.toLowerCase(),
      });
    };

    // Rail parity: every visible, navigable rail item is a palette command.
    for (const node of resolveNav(navCtx)) {
      if (node.kind === 'item') {
        if (!node.item.comingSoon) push(node.item.label, node.item.iconKey, node.item.path, undefined, LEGACY_KEYWORDS[node.item.key] ?? '');
        continue;
      }
      for (const it of node.group.items) {
        if (!it.comingSoon) push(it.label, it.iconKey, it.path, node.group.label, LEGACY_KEYWORDS[it.key] ?? '');
      }
      for (const sg of node.group.subgroups ?? []) {
        for (const it of sg.items) {
          if (!it.comingSoon) push(it.label, it.iconKey, it.path, `${node.group.label} · ${sg.label}`, LEGACY_KEYWORDS[it.key] ?? '');
        }
      }
    }

    // The personal trio. My Tickets rides the Support Tickets table's built-in
    // 'my-tickets' saved view (statusBucket 'my' = assigned to me, active) —
    // the modern surface, pre-filtered, per the 2026-07-31 direction. The
    // legacy /my-tickets page is no longer offered here.
    if (navCtx.hasTab('operations')) {
      push('My Tickets', 'My Tickets', '/operations/tickets?view=my-tickets', 'Personal', 'my tickets assigned to me support');
    }
    push('My Scorecard', 'My Scorecard', '/my-scorecard', 'Personal', 'my scorecard personal metrics kpi');
    push('My Time', 'My Time', '/my-time', 'Personal', 'my time personal timer hours');

    // Settings lives in the rail's bottom chrome, not the tree — add it here.
    push('Settings', 'Settings', '/settings', undefined, 'settings users roles permissions integrations');

    // R3.2: three discrete switch-to-X actions instead of a single toggle, so each
    // mode is independently searchable in the palette. The current mode's entry
    // is still listed (no-op if selected) for predictability.
    out.push(
      { type: 'nav', label: 'Switch to Light theme', icon: <LightModeIcon fontSize="small" />, action: () => onSetMode('light'), keywords: 'theme light mode' },
      { type: 'nav', label: 'Switch to Sepia theme', icon: <LocalCafeIcon fontSize="small" />, action: () => onSetMode('sepia'), keywords: 'theme sepia mode warm cream' },
      { type: 'nav', label: 'Switch to Dark theme', icon: <DarkModeIcon fontSize="small" />, action: () => onSetMode('dark'), keywords: 'theme dark mode' },
      { type: 'nav', label: 'Sync All Sources', icon: <SyncIcon fontSize="small" />, action: onSyncAll, keywords: 'sync update refresh data' },
    );
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navCtx, mode, onSetMode, onSyncAll]);

  const recent = useMemo(() => getRecentCommands(), [open, search]);
  const recentSet = useMemo(() => new Set(recent), [recent]);

  const displayList = useMemo<CommandItem[]>(() => {
    const q = search.toLowerCase();
    const filtered = commands.filter(c => !q || c.label.toLowerCase().includes(q) || c.keywords.includes(q));
    const recentFiltered = recent
      .map(label => commands.find(c => c.label === label))
      .filter((c): c is NavCommand => !!c && filtered.includes(c));
    const others = filtered.filter(c => !recentSet.has(c.label));
    const navList = q ? filtered : [...recentFiltered, ...others];
    const entityItems: EntityCommand[] = [];

    if (results && search.length >= 3) {
      (results.clients || []).forEach(c => entityItems.push({ type: 'entity', group: 'Clients', id: c.id, label: c.name, path: `/clients/${c.id}`, icon: NAV_ICONS.Clients }));
      // Roadmap 17h (2026-09-18): CRM contacts by name / company / email / phone digits → the contact record (THE GATE: /contacts/:id).
      (results.contacts || []).forEach(c => entityItems.push({ type: 'entity', group: 'Contacts', id: c.id, label: c.name, path: `/contacts/${c.id}`, icon: NAV_ICONS.Contacts, hint: c.company_name || c.email || c.phone || undefined }));
      // Canonical ticket deep link (THE GATE): the ticket modal opens from its
      // URL on the Support Tickets surface. (The pre-rebuild fallback pointed
      // thread-less tickets at /operations/todos?todo=<ticketId> — a todo the
      // id doesn't name.)
      (results.tickets || []).forEach(t => entityItems.push({ type: 'entity', group: 'Tickets', id: t.id, label: t.title, path: `/operations/tickets/${t.id}`, icon: NAV_ICONS.Tickets }));
      (results.meetings || []).forEach(m => entityItems.push({ type: 'entity', group: 'Meetings', id: m.id, label: m.title, path: `/meetings?open=${m.id}`, icon: NAV_ICONS.Meetings }));
      (results.leads || []).forEach(l => entityItems.push({ type: 'entity', group: 'Leads', id: l.id, label: l.name, path: '/sales/scraper', icon: NAV_ICONS.Sales }));
    }

    return [...navList, ...entityItems];
  }, [commands, recent, recentSet, results, search]);

  const executeItem = useCallback((item: CommandItem) => {
    closePalette();
    if (item.type === 'nav') {
      addRecentCommand(item.label);
      if (item.action) item.action();
      else if (item.path) navigate(item.path);
    } else {
      navigate(item.path);
    }
  }, [closePalette, navigate]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closePalette();
      return;
    }

    const total = displayList.length;
    if (e.key === 'ArrowDown' && total > 0) {
      e.preventDefault();
      const next = (selectedIndexRef.current + 1) % total;
      selectedIndexRef.current = next;
      setSelectedIndex(next);
      return;
    }

    if (e.key === 'ArrowUp' && total > 0) {
      e.preventDefault();
      const prev = (selectedIndexRef.current - 1 + total) % total;
      selectedIndexRef.current = prev;
      setSelectedIndex(prev);
      return;
    }

    if (e.key === 'Enter' && total > 0) {
      e.preventDefault();
      const item = displayList[selectedIndexRef.current];
      if (item) executeItem(item);
    }
  };

  let lastGroup = '';

  return (
    <Dialog
      open={open}
      onClose={closePalette}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '15%',
          transform: 'none',
          bgcolor: 'background.paper',
          borderRadius: '6px',
          border: '1px solid',
          borderColor: 'divider',
          maxHeight: '60vh',
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.5)' } } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <TextField
          inputRef={inputRef}
          autoFocus
          placeholder="Search actions, navigate..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          variant="standard"
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: (
              <Chip label="ESC" size="small" sx={{ fontSize: '10.5px', height: 20, bgcolor: 'action.hover' }} />
            ),
            disableUnderline: true,
            sx: { px: 2, py: 1.5, fontSize: '14px' },
          }}
          onKeyDown={handleKeyDown}
        />
        <Divider />
        <List dense sx={{ py: 0.5, maxHeight: 320, overflow: 'auto' }}>
          {loading && search.length >= 3 ? (
            <ListItem>
              <ListItemIcon sx={{ minWidth: 36 }}><CircularProgress size={20} /></ListItemIcon>
              <ListItemText primary="Searching..." primaryTypographyProps={{ color: 'text.secondary', fontSize: '13.5px' }} />
            </ListItem>
          ) : displayList.length === 0 ? (
            <ListItem>
              <ListItemText primary="No results" primaryTypographyProps={{ color: 'text.secondary', fontSize: '13.5px' }} />
            </ListItem>
          ) : (
            displayList.map((item, index) => {
              const isSelected = index === selectedIndex;
              const showGroupHeader = item.type === 'entity' && item.group !== lastGroup;
              if (item.type === 'entity') lastGroup = item.group;
              return (
                <React.Fragment key={item.type === 'nav' ? `nav-${item.label}-${index}` : `entity-${item.group}-${item.id}-${index}`}>
                  {showGroupHeader && (
                    <Box sx={{ px: 2, py: 0.5, pt: index > 0 ? 1.5 : 0.5 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '11px', textTransform: 'uppercase' }}>
                        {item.group}
                      </Typography>
                    </Box>
                  )}
                  <ListItem
                    ref={isSelected ? selectedItemRef : undefined}
                    selected={isSelected}
                    onClick={() => executeItem(item)}
                    sx={{
                      cursor: 'pointer',
                      px: 2,
                      py: 1,
                      bgcolor: isSelected ? 'action.selected' : undefined,
                      '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
                      borderRadius: 1,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '14px' }} />
                    {item.type === 'nav' && !search && recentSet.has(item.label) && (
                      <Chip icon={<AccessTimeIcon sx={{ fontSize: 14 }} />} label="Recent" size="small" sx={{ ml: 1, fontSize: '0.65rem', height: 20 }} />
                    )}
                    {item.type === 'nav' && !(!search && recentSet.has(item.label)) && item.path && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>{item.group ?? 'Navigate'}</Typography>
                    )}
                    {item.type === 'entity' && item.hint && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>{item.hint}</Typography>
                    )}
                  </ListItem>
                </React.Fragment>
              );
            })
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
}
